import type { Pessoa } from '@sheafyield/dominio';

export type CamposDaPessoa = {
  nome: string;
  endereco: string;
  telefone: string;
  idioma: string;
  status: Pessoa['status'];
  etiquetas: string;
};
const CAMPOS_DA_PESSOA = [
  'nome',
  'endereco',
  'telefone',
  'idioma',
  'status',
  'etiquetas',
] as const;
type IdentidadeDaFicha = Pick<Pessoa, 'id' | 'user_id'>;
export type CamposDoEstudo = {
  publicacao: string;
  licao: string;
  frequencia: string;
  data: string;
};
const CAMPOS_DO_ESTUDO = ['publicacao', 'licao', 'frequencia', 'data'] as const;
type Rascunho =
  | {
      tipo: 'estudo';
      valores: CamposDoEstudo;
      iniciais: CamposDoEstudo;
      iniciadoOriginal: string;
    }
  | {
      tipo: 'pessoa';
      valores: CamposDaPessoa;
      iniciais: CamposDaPessoa;
      tocados: (keyof CamposDaPessoa)[];
    }
  | { tipo: 'nota'; nota: string }
  | {
      tipo: 'agendar';
      texto: string;
      data: string;
      hora: string;
      agora: string;
      natural: boolean;
    };

function chave(pessoa: IdentidadeDaFicha, tipo: Rascunho['tipo']) {
  return `sheafyield:rascunho-ficha:${pessoa.user_id}:${pessoa.id}:${tipo}`;
}
/** A trava desmonta a árvore no segundo plano. O rascunho fica na sessão,
 * por conta/ficha/tipo, para o retorno não perder texto nem adotar o de outra
 * pessoa. A referência temporal do agendamento também precisa sobreviver. */
export function lerRascunhoDaFicha(
  pessoa: IdentidadeDaFicha,
  tipo: Rascunho['tipo'],
): Rascunho | null {
  try {
    const bruto = sessionStorage.getItem(chave(pessoa, tipo));
    if (bruto === null) return null;
    const valor: unknown = JSON.parse(bruto);
    if (typeof valor !== 'object' || valor === null) return null;
    const r = valor as Record<string, unknown>;
    if (r['tipo'] !== tipo) return null;
    if (
      tipo === 'pessoa' &&
      typeof r['valores'] === 'object' &&
      r['valores'] !== null &&
      typeof r['iniciais'] === 'object' &&
      r['iniciais'] !== null &&
      Array.isArray(r['tocados'])
    ) {
      const valores = r['valores'] as Record<string, unknown>;
      const iniciais = r['iniciais'] as Record<string, unknown>;
      if (
        CAMPOS_DA_PESSOA.every(
          (campo) =>
            typeof valores[campo] === 'string' &&
            typeof iniciais[campo] === 'string',
        ) &&
        [valores, iniciais].every((campos) =>
          ['interessado', 'revisita', 'estudo', 'nao_visitar'].includes(
            campos['status'] as string,
          ),
        ) &&
        r['tocados'].every((campo: unknown) =>
          CAMPOS_DA_PESSOA.includes(campo as keyof CamposDaPessoa),
        )
      )
        return {
          tipo,
          valores: valores as CamposDaPessoa,
          iniciais: iniciais as CamposDaPessoa,
          tocados: r['tocados'] as (keyof CamposDaPessoa)[],
        };
    }
    if (
      tipo === 'estudo' &&
      typeof r['iniciadoOriginal'] === 'string' &&
      !Number.isNaN(Date.parse(r['iniciadoOriginal']))
    ) {
      const valores = r['valores'] as Record<string, unknown> | undefined;
      const iniciais = r['iniciais'] as Record<string, unknown> | undefined;
      if (
        valores != null &&
        iniciais != null &&
        CAMPOS_DO_ESTUDO.every(
          (c) =>
            typeof valores[c] === 'string' && typeof iniciais[c] === 'string',
        )
      )
        return {
          tipo,
          valores: valores as CamposDoEstudo,
          iniciais: iniciais as CamposDoEstudo,
          iniciadoOriginal: r['iniciadoOriginal'],
        };
    }
    if (tipo === 'nota' && typeof r['nota'] === 'string')
      return { tipo, nota: r['nota'] };
    if (
      tipo === 'agendar' &&
      typeof r['texto'] === 'string' &&
      typeof r['data'] === 'string' &&
      typeof r['hora'] === 'string' &&
      typeof r['agora'] === 'string' &&
      !Number.isNaN(Date.parse(r['agora'])) &&
      typeof r['natural'] === 'boolean'
    ) {
      return {
        tipo,
        texto: r['texto'],
        data: r['data'],
        hora: r['hora'],
        agora: r['agora'],
        natural: r['natural'],
      };
    }
    return null;
  } catch {
    return null;
  }
}
/** Síncrono, a cada edição: um atraso perderia a última tecla ao travar. */
export function gravarRascunhoDaFicha(
  pessoa: IdentidadeDaFicha,
  rascunho: Rascunho,
): void {
  try {
    sessionStorage.setItem(
      chave(pessoa, rascunho.tipo),
      JSON.stringify(rascunho),
    );
  } catch {
    /* Cota ou janela privada não podem impedir o formulário. */
  }
}
export function limparRascunhoDaFicha(
  pessoa: IdentidadeDaFicha,
  tipo: Rascunho['tipo'],
): void {
  try {
    sessionStorage.removeItem(chave(pessoa, tipo));
  } catch {
    /* A escrita principal já está no banco, mesmo sem armazenamento de sessão. */
  }
}

export type FolhaAtivaDaFicha =
  Exclude<Rascunho['tipo'], 'estudo'> | `estudo:${string}`;
function chaveDaFolhaAtiva(pessoa: IdentidadeDaFicha): string {
  return `sheafyield:folha-ativa-ficha:${pessoa.user_id}:${pessoa.id}`;
}
/** Rascunho não significa formulário aberto: fechar pode guardar texto sem
 * pedir retomada. Só este marcador recompõe a folha desmontada pela trava. */
export function lerFolhaAtivaDaFicha(
  pessoa: IdentidadeDaFicha,
): FolhaAtivaDaFicha | null {
  try {
    const tipo = sessionStorage.getItem(chaveDaFolhaAtiva(pessoa));
    return tipo === 'nota' ||
      tipo === 'agendar' ||
      tipo === 'pessoa' ||
      tipo?.startsWith('estudo:')
      ? (tipo as FolhaAtivaDaFicha)
      : null;
  } catch {
    return null;
  }
}
/** O unmount involuntário não limpa o marcador. Somente o fechamento
 * explícito e o sucesso gravam null; fazê-lo no cleanup repetiria a perda
 * de contexto a cada ida ao segundo plano no iPhone. */
export function marcarFolhaAtivaDaFicha(
  pessoa: IdentidadeDaFicha,
  tipo: FolhaAtivaDaFicha | null,
): void {
  try {
    const chave = chaveDaFolhaAtiva(pessoa);
    if (tipo === null) sessionStorage.removeItem(chave);
    else sessionStorage.setItem(chave, tipo);
  } catch {
    // O armazenamento indisponível não pode impedir abrir ou fechar.
  }
}
